/* eslint-disable import/prefer-default-export */
import { applyMiddleware, createStore, compose } from 'redux';
import thunkMiddleware from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createLogger } from 'redux-logger';
import rootReducer from 'src/reducers';
import {
  selectPersistableFive9,
  PERSIST_SCHEMA_VERSION,
  LOG_CAP
} from 'src/reducers/five9Reducer';
import { looksLikeCallSid } from 'src/utils/five9/callSidValidation';

const ENABLE_REDUX_LOGGER = false;

// --- Five9 sessionStorage rehydration ---------------------------------------
// The Five9 slice persists ONLY `calls` + `activeCallSid` for the lifetime of
// the tab so a hard reload while on a call preserves the call/dispatch
// association until the SDK re-registers and re-probes. The `log` slice is
// intentionally in-memory only.
const FIVE9_STORAGE_KEY = 'five9:v1';

/**
 * Remove the persisted Five9 snapshot from sessionStorage. Called on auth
 * transitions (logout, user switch) so a subsequent authenticated session
 * in the same tab does not rehydrate the previous user's call/dispatch
 * associations. Safe to call before sessionStorage exists (SSR) or when
 * no snapshot has been written yet.
 *
 * @returns {void}
 */
export const clearPersistedFive9 = () => {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(FIVE9_STORAGE_KEY);
  } catch (_) { /* noop */ }
};

export const readPersistedFive9 = (currentUserId) => {
  try {
    if (typeof sessionStorage === 'undefined') return undefined;
    const raw = sessionStorage.getItem(FIVE9_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== PERSIST_SCHEMA_VERSION) {
      sessionStorage.removeItem(FIVE9_STORAGE_KEY);
      return undefined;
    }
    if (currentUserId == null) return undefined;
    const ownerUserId = parsed?.ownerUserId ?? null;
    if (ownerUserId == null || String(ownerUserId) !== String(currentUserId)) {
      sessionStorage.removeItem(FIVE9_STORAGE_KEY);
      return undefined;
    }
    const rawCalls = parsed.calls || {};
    const calls = {};
    Object.keys(rawCalls).forEach((k) => {
      if (looksLikeCallSid(k)) calls[k] = rawCalls[k];
    });
    const rawActive = parsed.activeCallSid || null;
    const activeCallSid = looksLikeCallSid(rawActive) && calls[rawActive] ? rawActive : null;
    return {
      calls,
      activeCallSid,
      log: [],
      logCap: LOG_CAP
    };
  } catch (_) {
    return undefined;
  }
};

const attachFive9Persistence = (store) => {
  if (typeof sessionStorage === 'undefined') return;
  let scheduled = false;
  let lastSerialized = '';
  const flush = () => {
    scheduled = false;
    try {
      const ownerUserId = store.getState()?.account?.user?.id ?? null;
      if (ownerUserId == null) {
        sessionStorage.removeItem(FIVE9_STORAGE_KEY);
        lastSerialized = '';
        return;
      }
      const snapshot = selectPersistableFive9(store.getState());
      const serialized = JSON.stringify(snapshot);
      if (serialized === lastSerialized) return;
      lastSerialized = serialized;
      sessionStorage.setItem(FIVE9_STORAGE_KEY, serialized);
    } catch (_) { /* noop */ }
  };
  store.subscribe(() => {
    if (scheduled) return;
    scheduled = true;
    // Debounce with a short timeout — 250ms is well below human reaction
    // time and easily fast enough that a reload picks up recent state.
    setTimeout(flush, 250);
  });
};

const loggerMiddleware = createLogger();
export function configureStore(preloadedState = {}) {
  const middlewares = [thunkMiddleware];

  if (ENABLE_REDUX_LOGGER) {
    middlewares.push(loggerMiddleware);
  }

  const middlewareEnhancer = composeWithDevTools(
    applyMiddleware(...middlewares)
  );

  const enhancers = [middlewareEnhancer];
  const composedEnhancers = compose(...enhancers);

  const store = createStore(rootReducer, preloadedState, composedEnhancers);
  attachFive9Persistence(store);

  return store;
}

export const store = configureStore();
